import { useState, useEffect, useRef } from "react";
import { BarChart3, DollarSign, Users, Eye, ShoppingCart, Timer, LogOut as BounceIcon, MousePointerClick, Mail, Phone, UserPlus, UserCheck, LogIn, ArrowDownToLine, Route, ArrowUp, ArrowDown, Layers, ChevronRight, Radio, MapPin, Monitor, Smartphone, Tablet, Clock } from "lucide-react";
import { AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { getAnalyticsSales, getAnalyticsTraffic, getAnalyticsReturningVisitors, getAnalyticsReturningVisitorsByProduct, getAnalyticsReturningVisitorsByProductDetail, getAnalyticsBehaviour, getAnalyticsProductReturningVisitors, getAnalyticsPageReturningVisitors, getAnalyticsPageTrend, getAnalyticsWeeklyDigestPreview, getAnalyticsAbandonedCarts, getAbandonedCartDetail, getAnalyticsLive, getAnalyticsLiveHistory, resetAnalytics } from "@/admin/adminApi";

const BRAND = "#C2B280";
const ACCENT = "#6B8CAE";

const TABS = [
  { key: "sales", label: "Sales Overview" },
  { key: "traffic", label: "Traffic Overview" },
  { key: "behaviour", label: "Behaviour Overview" },
  { key: "live", label: "Live" },
  { key: "abandoned", label: "Abandoned Carts" },
];

const RANGES = [
  { key: "today", label: "Today" },
  { key: "yesterday", label: "Yesterday" },
  { key: "48h", label: "48H" },
  { key: "7d", label: "7D" },
  { key: "30d", label: "30D" },
  { key: "90d", label: "90D" },
];

const fmtMoney = (cents) => `$${(cents / 100).toLocaleString("en-NZ", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (iso) => new Date(iso).toLocaleDateString("en-NZ", { day: "numeric", month: "short" });
const fmtDuration = (seconds) => {
  if (!seconds) return "—";
  const m = Math.floor(seconds / 60);
  const s = Math.round(seconds % 60);
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
};
const timeAgo = (iso) => {
  const mins = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.round(hours / 24)}d ago`;
};

const ChangeBadge = ({ percent }) => {
  if (percent === null || percent === undefined) return null;
  const isUp = percent >= 0;
  return (
    <span
      data-testid="analytics-visits-change"
      className={`inline-flex items-center gap-0.5 text-xs font-mono ${isUp ? "text-emerald-400" : "text-red-400"}`}
    >
      {isUp ? <ArrowUp size={11} /> : <ArrowDown size={11} />}
      {Math.abs(percent)}%
    </span>
  );
};

const StatCard = ({ icon: Icon, label, value, testId, changePercent, onClick }) => (
  <div
    data-testid={testId}
    onClick={onClick}
    className={`border border-[var(--border-default)] bg-[var(--bg-secondary)] p-5 ${onClick ? "cursor-pointer hover:border-[var(--brand-primary)]/50 transition-colors" : ""}`}
  >
    <div className="flex items-center gap-2 text-white/50 text-xs font-mono uppercase tracking-widest mb-2">
      <Icon size={14} className="text-[var(--brand-primary)]" /> {label}
    </div>
    <div className="flex items-baseline gap-2">
      <span className="font-display text-2xl text-white">{value}</span>
      <ChangeBadge percent={changePercent} />
    </div>
    {changePercent !== null && changePercent !== undefined && (
      <p className="text-white/30 text-[10px] font-mono uppercase tracking-widest mt-1">vs previous period</p>
    )}
    {onClick && <p className="text-[var(--brand-primary)] text-[10px] font-mono uppercase tracking-widest mt-1">View where they went →</p>}
  </div>
);

const RangeSelector = ({ range, setRange }) => (
  <div className="flex items-center gap-1 overflow-x-auto">
    {RANGES.map((r) => (
      <button
        key={r.key}
        data-testid={`analytics-range-${r.key}`}
        onClick={() => setRange(r.key)}
        className={`shrink-0 px-3 py-1.5 text-xs font-mono uppercase tracking-widest border transition-colors ${
          range === r.key ? "border-[var(--brand-primary)] text-[var(--brand-primary)]" : "border-transparent text-white/50 hover:text-white"
        }`}
      >
        {r.label}
      </button>
    ))}
  </div>
);

const EmptyState = ({ label }) => <div className="text-center py-12 text-white/40 text-sm font-mono">{label}</div>;

function SalesTab({ range }) {
  const [data, setData] = useState(null);
  useEffect(() => { getAnalyticsSales(range).then(setData); }, [range]);
  if (!data) return <EmptyState label="Loading…" />;
  return (
    <div data-testid="analytics-sales-tab">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
        <StatCard icon={DollarSign} label="Revenue" value={fmtMoney(data.totalRevenueCents)} testId="analytics-total-revenue" />
        <StatCard icon={ShoppingCart} label="Orders" value={data.orderCount} testId="analytics-order-count" />
      </div>
      {data.dailyRevenue.length === 0 ? (
        <EmptyState label="No paid orders in this period yet." />
      ) : (
        <div className="border border-[var(--border-default)] bg-[var(--bg-secondary)] p-5 mb-8">
          <p className="text-xs font-mono uppercase tracking-widest text-white/50 mb-4">Revenue Over Time</p>
          <ResponsiveContainer width="100%" height={260}>
            <AreaChart data={data.dailyRevenue.map((d) => ({ ...d, date: fmtDate(d.date), revenue: d.revenueCents / 100 }))}>
              <defs>
                <linearGradient id="salesGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={BRAND} stopOpacity={0.4} />
                  <stop offset="100%" stopColor={BRAND} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid stroke="#27272A" vertical={false} />
              <XAxis dataKey="date" stroke="#666" fontSize={11} />
              <YAxis stroke="#666" fontSize={11} tickFormatter={(v) => `$${v}`} />
              <Tooltip contentStyle={{ background: "#141414", border: "1px solid #27272A" }} formatter={(v) => [`$${v.toFixed(2)}`, "Revenue"]} />
              <Area type="monotone" dataKey="revenue" stroke={BRAND} fill="url(#salesGrad)" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}
      <p className="text-xs font-mono uppercase tracking-widest text-white/50 mb-3">Top Selling Products</p>
      {data.topProducts.length === 0 ? (
        <EmptyState label="No sales yet." />
      ) : (
        <div className="border border-[var(--border-default)] divide-y divide-[var(--border-default)]">
          {data.topProducts.map((p, i) => (
            <div key={i} data-testid={`analytics-sales-product-${i}`} className="flex items-center justify-between p-4">
              <div className="text-white text-sm">{p.name}</div>
              <div className="flex items-center gap-6 text-xs font-mono">
                <span className="text-white/50">Qty {p.quantity}</span>
                <span className="text-[var(--brand-primary)]">{fmtMoney(p.revenueCents)}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ReturningVisitorsByProductDetailDialog({ row, range, onClose }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!row) { setData(null); return; }
    setData(null);
    getAnalyticsReturningVisitorsByProductDetail(range, { categorySlug: row.category_slug, slug: row.slug, path: row.path }).then(setData);
  }, [row, range]);

  return (
    <Dialog open={!!row} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-lg bg-[var(--bg-secondary)] border-[var(--border-default)] text-white" data-testid="returning-visitors-by-product-detail-dialog">
        <DialogHeader>
          <DialogTitle className="text-white">Returning Visitors — {row?.name}</DialogTitle>
          <DialogDescription className="text-white/50">
            Returning visitors who viewed this in the selected period, and everywhere else they went on the site.
          </DialogDescription>
        </DialogHeader>
        {!data ? <EmptyState label="Loading…" /> : data.visitors.length === 0 ? (
          <EmptyState label="No returning visitors viewed this in this period." />
        ) : (
          <div className="space-y-5 max-h-[60vh] overflow-y-auto">
            {data.visitors.map((v, vi) => (
              <div key={v.visitor_id} data-testid={`returning-visitors-by-product-detail-${vi}`} className="border border-[var(--border-default)] p-4">
                <span className="text-xs font-mono uppercase tracking-widest text-white/50">Visitor {vi + 1}</span>
                <div className="space-y-1.5 mt-3">
                  {v.timeline.map((t, ti) => (
                    <div key={ti} data-testid={`returning-visitors-by-product-detail-${vi}-visit-${ti}`} className="flex items-center justify-between text-xs gap-3">
                      <span className="font-mono text-white/70 truncate">{t.path}</span>
                      <span className="text-white/30 shrink-0 font-mono">
                        {new Date(t.created_at).toLocaleString("en-NZ", { day: "numeric", month: "short", hour: "numeric", minute: "2-digit", timeZone: "Pacific/Auckland" })}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function ReturningVisitorsDialog({ open, range, onClose, onSelectSession }) {
  const [view, setView] = useState("product");
  const [data, setData] = useState(null);
  const [byProduct, setByProduct] = useState(null);
  const [drilldownRow, setDrilldownRow] = useState(null);

  useEffect(() => {
    if (!open) { setData(null); setByProduct(null); setDrilldownRow(null); setView("product"); return; }
    getAnalyticsReturningVisitors(range).then(setData);
    getAnalyticsReturningVisitorsByProduct(range).then(setByProduct);
  }, [open, range]);

  return (
    <>
      <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
        <DialogContent data-testid="returning-visitors-dialog" className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-display text-xl text-white">Returning Visitors</DialogTitle>
            <DialogDescription className="sr-only">
              Returning visitors in this period — grouped by product/page, or listed individually with the pages they visited.
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-2 mb-2" data-testid="returning-visitors-view-toggle">
            <button
              type="button"
              data-testid="returning-visitors-view-by-product"
              onClick={() => setView("product")}
              className={`text-xs font-mono uppercase tracking-widest px-3 py-1.5 border transition-colors ${view === "product" ? "border-[var(--brand-primary)] text-[var(--brand-primary)]" : "border-[var(--border-default)] text-white/50 hover:text-white"}`}
            >
              By Product
            </button>
            <button
              type="button"
              data-testid="returning-visitors-view-by-visitor"
              onClick={() => setView("visitor")}
              className={`text-xs font-mono uppercase tracking-widest px-3 py-1.5 border transition-colors ${view === "visitor" ? "border-[var(--brand-primary)] text-[var(--brand-primary)]" : "border-[var(--border-default)] text-white/50 hover:text-white"}`}
            >
              By Visitor
            </button>
          </div>

          {view === "product" ? (
            !byProduct ? (
              <EmptyState label="Loading…" />
            ) : byProduct.products.length === 0 ? (
              <EmptyState label="No returning visitors in this period." />
            ) : (
              <>
                <p className="text-xs text-white/40 mb-2" data-testid="returning-visitors-by-product-summary-note">
                  {byProduct.totalReturningVisitors} returning visitor{byProduct.totalReturningVisitors === 1 ? "" : "s"} — % is the share of them who viewed each product/page (a visitor can view more than one, so these won't add up to 100%). Arrow shows the trend vs. the previous period of equal length.
                </p>
                <div className="border border-[var(--border-default)] divide-y divide-[var(--border-default)] max-h-96 overflow-y-auto">
                  {byProduct.products.map((p, i) => (
                    <button
                      key={p.category_slug ? `${p.category_slug}/${p.slug}` : p.path}
                      type="button"
                      data-testid={`returning-visitors-by-product-row-${i}`}
                      onClick={() => setDrilldownRow(p)}
                      className="w-full flex items-center justify-between p-3 text-sm text-left hover:bg-white/5 transition-colors gap-3"
                    >
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        {p.thumb && <img src={p.thumb} alt={p.name} className="w-9 h-9 object-cover shrink-0 border border-[var(--border-default)]" />}
                        <span className="text-white truncate">{p.name}</span>
                      </div>
                      <div className="flex items-center gap-3 shrink-0">
                        <span data-testid={`returning-visitors-by-product-count-${i}`} className="text-xs font-mono px-1.5 py-0.5 rounded-full bg-[var(--brand-primary)]/15 text-[var(--brand-primary)]">
                          {p.visitorCount} ({p.percent}%)
                        </span>
                        {p.trend === "up" && <ArrowUp size={14} className="text-emerald-400 shrink-0" data-testid={`returning-visitors-by-product-trend-up-${i}`} />}
                        {p.trend === "down" && <ArrowDown size={14} className="text-red-400 shrink-0" data-testid={`returning-visitors-by-product-trend-down-${i}`} />}
                        {p.trend === "flat" && <span className="text-white/30 text-xs shrink-0" data-testid={`returning-visitors-by-product-trend-flat-${i}`}>—</span>}
                        <ChevronRight size={16} className="text-white/30" />
                      </div>
                    </button>
                  ))}
                </div>
              </>
            )
          ) : !data ? (
            <EmptyState label="Loading…" />
          ) : data.sessions.length === 0 ? (
            <EmptyState label="No returning visitors in this period." />
          ) : (
            <div className="border border-[var(--border-default)] divide-y divide-[var(--border-default)] max-h-96 overflow-y-auto">
              {data.sessions.map((s, i) => (
                <button
                  key={s.visitor_id}
                  type="button"
                  data-testid={`returning-visitor-row-${i}`}
                  onClick={() => onSelectSession(s.session_id)}
                  className="w-full flex items-center justify-between p-3 text-sm text-left hover:bg-white/5 transition-colors gap-3"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="text-white truncate">{s.entry_path}{s.entry_path !== s.exit_path && ` → ${s.exit_path}`}</p>
                      {s.visit_count > 1 && (
                        <span data-testid={`returning-visitor-visit-count-${i}`} className="shrink-0 text-[10px] font-mono px-1.5 py-0.5 rounded-full bg-[var(--brand-primary)]/15 text-[var(--brand-primary)]">
                          {s.visit_count} visits
                        </span>
                      )}
                    </div>
                    <p className="text-white/40 text-xs font-mono mt-0.5 capitalize">
                      {s.source} · {s.device} · {s.pageview_count} page{s.pageview_count === 1 ? "" : "s"} · last seen {new Date(s.started_at).toLocaleString("en-NZ", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
                    </p>
                  </div>
                  <ChevronRight size={16} className="text-white/30 shrink-0" />
                </button>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
      <ReturningVisitorsByProductDetailDialog row={drilldownRow} range={range} onClose={() => setDrilldownRow(null)} />
    </>
  );
}

function TrafficTab({ range }) {
  const [data, setData] = useState(null);
  const [showReturning, setShowReturning] = useState(false);
  const [activeSessionId, setActiveSessionId] = useState(null);
  useEffect(() => { getAnalyticsTraffic(range).then(setData); }, [range]);
  if (!data) return <EmptyState label="Loading…" />;
  return (
    <div data-testid="analytics-traffic-tab">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
        <StatCard icon={Users} label="Sessions" value={data.totalVisits} testId="analytics-total-visits" changePercent={data.visitsChangePercent} />
        <StatCard icon={UserCheck} label="Unique Visitors" value={data.totalUniqueVisitors} testId="analytics-unique-visitors" changePercent={data.uniqueVisitorsChangePercent} />
        <StatCard icon={Eye} label="Page Views" value={data.totalPageviews} testId="analytics-total-pageviews" changePercent={data.pageviewsChangePercent} />
        <StatCard icon={BounceIcon} label="Bounce Rate" value={`${data.bounceRatePercent}%`} testId="analytics-bounce-rate" />
        <StatCard icon={Timer} label="Avg. Session" value={fmtDuration(data.avgSessionDurationSeconds)} testId="analytics-avg-session-duration" />
        <StatCard icon={Layers} label="Pages / Session" value={data.avgPagesPerSession} testId="analytics-avg-pages-per-session" />
        <StatCard icon={UserPlus} label="New Visitors" value={data.newVisitors} testId="analytics-new-visitors" />
        <StatCard icon={UserCheck} label="Returning Visitors" value={data.returningVisitors} testId="analytics-returning-visitors" onClick={() => setShowReturning(true)} />
      </div>
      {data.dailyVisits.length === 0 ? (
        <EmptyState label="No visits tracked yet in this period." />
      ) : (
        <div className="border border-[var(--border-default)] bg-[var(--bg-secondary)] p-5 mb-8">
          <p className="text-xs font-mono uppercase tracking-widest text-white/50 mb-4">Visits Over Time</p>
          <ResponsiveContainer width="100%" height={260}>
            <AreaChart data={data.dailyVisits.map((d) => ({ ...d, date: fmtDate(d.date) }))}>
              <defs>
                <linearGradient id="trafficGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={ACCENT} stopOpacity={0.4} />
                  <stop offset="100%" stopColor={ACCENT} stopOpacity={0} />
                </linearGradient>
                <linearGradient id="uniqueVisitorsGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={BRAND} stopOpacity={0.35} />
                  <stop offset="100%" stopColor={BRAND} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid stroke="#27272A" vertical={false} />
              <XAxis dataKey="date" stroke="#666" fontSize={11} />
              <YAxis stroke="#666" fontSize={11} allowDecimals={false} />
              <Tooltip contentStyle={{ background: "#141414", border: "1px solid #27272A" }} />
              <Area type="monotone" dataKey="visits" name="Sessions" stroke={ACCENT} fill="url(#trafficGrad)" strokeWidth={2} />
              <Area type="monotone" dataKey="uniqueVisitors" name="Unique Visitors" stroke={BRAND} fill="url(#uniqueVisitorsGrad)" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
          <div className="flex items-center gap-4 mt-3 text-xs font-mono">
            <span className="flex items-center gap-1.5 text-white/50"><span className="w-2.5 h-2.5 rounded-full" style={{ background: ACCENT }} /> Sessions</span>
            <span className="flex items-center gap-1.5 text-white/50"><span className="w-2.5 h-2.5 rounded-full" style={{ background: BRAND }} /> Unique Visitors</span>
          </div>
        </div>
      )}
      {data.sessionsByDayOfWeek?.length > 0 && (
        <div className="border border-[var(--border-default)] bg-[var(--bg-secondary)] p-5 mb-8">
          <p className="text-xs font-mono uppercase tracking-widest text-white/50 mb-4">Avg. Sessions By Day Of Week</p>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={data.sessionsByDayOfWeek}>
              <CartesianGrid stroke="#27272A" vertical={false} />
              <XAxis dataKey="day" stroke="#666" fontSize={11} />
              <YAxis stroke="#666" fontSize={11} allowDecimals={false} />
              <Tooltip contentStyle={{ background: "#141414", border: "1px solid #27272A" }} formatter={(v) => [v, "Avg. sessions"]} />
              <Bar dataKey="avgSessions" fill={BRAND} radius={[2, 2, 0, 0]} data-testid="analytics-day-of-week-bar" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <p className="text-xs font-mono uppercase tracking-widest text-white/50 mb-3">Traffic Sources</p>
          {data.sources.length === 0 ? <EmptyState label="No data yet." /> : (
            <div className="border border-[var(--border-default)] divide-y divide-[var(--border-default)]">
              {data.sources.map((s, i) => (
                <div key={s.source || i} data-testid={`analytics-source-${s.source}`} className="flex items-center justify-between p-3 text-sm">
                  <span className="text-white capitalize">{s.source}</span>
                  <span className="font-mono text-[var(--brand-primary)]">{s.count}</span>
                </div>
              ))}
            </div>
          )}
        </div>
        <div>
          <p className="text-xs font-mono uppercase tracking-widest text-white/50 mb-3">Devices</p>
          {data.devices.length === 0 ? <EmptyState label="No data yet." /> : (
            <div className="border border-[var(--border-default)] divide-y divide-[var(--border-default)]">
              {data.devices.map((d, i) => (
                <div key={d.device || i} data-testid={`analytics-device-${d.device}`} className="flex items-center justify-between p-3 text-sm">
                  <span className="text-white capitalize">{d.device}</span>
                  <span className="font-mono text-[var(--brand-primary)]">{d.count}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      <ReturningVisitorsDialog
        open={showReturning}
        range={range}
        onClose={() => setShowReturning(false)}
        onSelectSession={(sid) => { setShowReturning(false); setActiveSessionId(sid); }}
      />
      <VisitorHistoryDialog sessionId={activeSessionId} onClose={() => setActiveSessionId(null)} />
    </div>
  );
}

const PathList = ({ items, testIdPrefix, emptyLabel }) =>
  items.length === 0 ? <EmptyState label={emptyLabel} /> : (
    <div className="border border-[var(--border-default)] divide-y divide-[var(--border-default)]">
      {items.map((item, i) => (
        <div key={item.path || i} data-testid={`${testIdPrefix}-${i}`} className="flex items-center justify-between p-3 text-sm gap-3">
          <span className="text-white font-mono truncate">{item.path}</span>
          <span className="text-[var(--brand-primary)] font-mono shrink-0">{item.count}</span>
        </div>
      ))}
    </div>
  );

const FlowStep = ({ step }) => (
  <div data-testid={`analytics-flow-step-${step.step}`} className="border border-[var(--border-default)] bg-[var(--bg-secondary)] p-4 w-64 shrink-0">
    <div className="flex items-center justify-between mb-3">
      <span className="text-xs font-mono uppercase tracking-widest text-white/50">Step {step.step}</span>
      <span className="text-xs font-mono text-white/40">{step.totalSessions} sessions</span>
    </div>
    <div className="space-y-2.5">
      {step.rows.map((r, i) => (
        <div key={r.path || i} data-testid={`analytics-flow-step-${step.step}-row-${i}`}>
          <div className="flex items-center justify-between text-xs mb-1 gap-2">
            <span className="text-white font-mono truncate">{r.path}</span>
            <span className="text-[var(--brand-primary)] font-mono shrink-0">{r.percent}%</span>
          </div>
          <div className="h-1 bg-white/10">
            <div className="h-1 bg-[var(--brand-primary)]" style={{ width: `${r.percent}%` }} />
          </div>
        </div>
      ))}
    </div>
    {step.droppedOff > 0 && (
      <p data-testid={`analytics-flow-step-${step.step}-dropoff`} className="text-white/30 text-[10px] font-mono uppercase tracking-widest mt-3 pt-3 border-t border-[var(--border-default)]">
        {step.droppedOff} left the site here ({step.droppedOffPercent}%)
      </p>
    )}
  </div>
);

function ReturningVisitorDialog({ product, range, onClose }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!product) { setData(null); return; }
    setData(null);
    getAnalyticsProductReturningVisitors(product.category_slug, product.slug, range).then(setData);
  }, [product, range]);

  return (
    <Dialog open={!!product} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-lg bg-[var(--bg-secondary)] border-[var(--border-default)] text-white" data-testid="returning-visitor-dialog">
        <DialogHeader>
          <DialogTitle className="text-white">Returning Viewers — {product?.name}</DialogTitle>
          <DialogDescription className="text-white/50">
            Visitors who viewed this product on 2+ different days, and everywhere else they went on the site.
          </DialogDescription>
        </DialogHeader>
        {!data ? <EmptyState label="Loading…" /> : data.visitors.length === 0 ? (
          <EmptyState label="No returning viewers in this period." />
        ) : (
          <div className="space-y-5 max-h-[60vh] overflow-y-auto">
            {data.visitors.map((v, vi) => (
              <div key={v.visitor_id} data-testid={`returning-visitor-${vi}`} className="border border-[var(--border-default)] p-4">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-xs font-mono uppercase tracking-widest text-white/50">Visitor {vi + 1}</span>
                  <span className="text-xs font-mono text-[var(--brand-primary)]">{v.daysViewed} days viewed</span>
                </div>
                <div className="space-y-1.5">
                  {v.timeline.map((t, ti) => (
                    <div key={ti} data-testid={`returning-visitor-${vi}-visit-${ti}`} className="flex items-center justify-between text-xs gap-3">
                      <span className="font-mono text-white/70 truncate">{t.path}</span>
                      <span className="text-white/30 shrink-0 font-mono">
                        {new Date(t.created_at).toLocaleString("en-NZ", { day: "numeric", month: "short", hour: "numeric", minute: "2-digit", timeZone: "Pacific/Auckland" })}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function PageReturningVisitorDialog({ page, range, onClose }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!page) { setData(null); return; }
    setData(null);
    getAnalyticsPageReturningVisitors(page.path, range).then(setData);
  }, [page, range]);

  return (
    <Dialog open={!!page} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-lg bg-[var(--bg-secondary)] border-[var(--border-default)] text-white" data-testid="page-returning-visitor-dialog">
        <DialogHeader>
          <DialogTitle className="text-white">Returning Viewers — {page?.path}</DialogTitle>
          <DialogDescription className="text-white/50">
            Visitors who viewed this page on 2+ different days, and everywhere else they went on the site.
          </DialogDescription>
        </DialogHeader>
        {!data ? <EmptyState label="Loading…" /> : data.visitors.length === 0 ? (
          <EmptyState label="No returning viewers in this period." />
        ) : (
          <div className="space-y-5 max-h-[60vh] overflow-y-auto">
            {data.visitors.map((v, vi) => (
              <div key={v.visitor_id} data-testid={`page-returning-visitor-${vi}`} className="border border-[var(--border-default)] p-4">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-xs font-mono uppercase tracking-widest text-white/50">Visitor {vi + 1}</span>
                  <span className="text-xs font-mono text-[var(--brand-primary)]">{v.daysViewed} days viewed</span>
                </div>
                <div className="space-y-1.5">
                  {v.timeline.map((t, ti) => (
                    <div key={ti} data-testid={`page-returning-visitor-${vi}-visit-${ti}`} className="flex items-center justify-between text-xs gap-3">
                      <span className="font-mono text-white/70 truncate">{t.path}</span>
                      <span className="text-white/30 shrink-0 font-mono">
                        {new Date(t.created_at).toLocaleString("en-NZ", { day: "numeric", month: "short", hour: "numeric", minute: "2-digit", timeZone: "Pacific/Auckland" })}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function WeeklyDigestPreviewDialog({ open, onClose }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!open) { setData(null); return; }
    getAnalyticsWeeklyDigestPreview().then(setData);
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl bg-[var(--bg-secondary)] border-[var(--border-default)] text-white" data-testid="weekly-digest-preview-dialog">
        <DialogHeader>
          <DialogTitle className="text-white">Weekly Interest Digest Preview</DialogTitle>
          <DialogDescription className="text-white/50">
            Exactly what would be emailed to the admin inbox if the digest went out right now.
          </DialogDescription>
        </DialogHeader>
        {!data ? <EmptyState label="Loading…" /> : (
          <iframe
            title="Weekly digest preview"
            data-testid="weekly-digest-preview-frame"
            srcDoc={data.html}
            className="w-full h-[60vh] bg-white border border-[var(--border-default)]"
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

const TREND_RANGES = [
  { key: "7d", label: "7D" },
  { key: "30d", label: "30D" },
  { key: "90d", label: "90D" },
];

function PageTrendDialog({ page, onClose }) {
  const [trendRange, setTrendRange] = useState("30d");
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!page) { setData(null); return; }
    setData(null);
    getAnalyticsPageTrend(page.path, trendRange).then(setData);
  }, [page, trendRange]);

  return (
    <Dialog open={!!page} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-2xl bg-[var(--bg-secondary)] border-[var(--border-default)] text-white" data-testid="page-trend-dialog">
        <DialogHeader>
          <div className="flex items-center justify-between gap-4 pr-6">
            <DialogTitle className="text-white truncate">Page Trend — {page?.path}</DialogTitle>
            <div className="flex items-center gap-1 shrink-0">
              {TREND_RANGES.map((r) => (
                <button
                  key={r.key}
                  type="button"
                  onClick={() => setTrendRange(r.key)}
                  data-testid={`page-trend-range-${r.key}`}
                  className={`text-[10px] font-mono uppercase tracking-widest px-2.5 py-1 border transition-colors ${
                    trendRange === r.key
                      ? "border-[var(--brand-primary)] text-[var(--brand-primary)]"
                      : "border-[var(--border-default)] text-white/40 hover:text-white/70"
                  }`}
                >
                  {r.label}
                </button>
              ))}
            </div>
          </div>
          <DialogDescription className="text-white/50">
            Daily views, traffic sources, and how this period compares to the one before it — useful for spotting the impact of a new ad campaign.
          </DialogDescription>
        </DialogHeader>
        {!data ? <EmptyState label="Loading…" /> : (
          <div className="space-y-6">
            <div className="grid grid-cols-2 gap-4">
              <StatCard icon={Eye} label="Views" value={data.totalViews} testId="page-trend-views" changePercent={data.viewsChangePercent} />
              <StatCard icon={UserCheck} label="Unique Visitors" value={data.totalUniqueVisitors} testId="page-trend-unique-visitors" changePercent={data.uniqueVisitorsChangePercent} />
            </div>
            {data.dailyViews.length === 0 ? (
              <EmptyState label="No views tracked yet in this period." />
            ) : (
              <div className="border border-[var(--border-default)] p-5">
                <p className="text-xs font-mono uppercase tracking-widest text-white/50 mb-4">Views Over Time</p>
                <ResponsiveContainer width="100%" height={220}>
                  <AreaChart data={data.dailyViews.map((d) => ({ ...d, date: fmtDate(d.date) }))}>
                    <defs>
                      <linearGradient id="pageTrendViewsGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={ACCENT} stopOpacity={0.4} />
                        <stop offset="100%" stopColor={ACCENT} stopOpacity={0} />
                      </linearGradient>
                      <linearGradient id="pageTrendUniqueGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={BRAND} stopOpacity={0.35} />
                        <stop offset="100%" stopColor={BRAND} stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid stroke="#27272A" vertical={false} />
                    <XAxis dataKey="date" stroke="#666" fontSize={11} />
                    <YAxis stroke="#666" fontSize={11} allowDecimals={false} />
                    <Tooltip contentStyle={{ background: "#141414", border: "1px solid #27272A" }} />
                    <Area type="monotone" dataKey="views" name="Views" stroke={ACCENT} fill="url(#pageTrendViewsGrad)" strokeWidth={2} />
                    <Area type="monotone" dataKey="uniqueVisitors" name="Unique Visitors" stroke={BRAND} fill="url(#pageTrendUniqueGrad)" strokeWidth={2} />
                  </AreaChart>
                </ResponsiveContainer>
                <div className="flex items-center gap-4 mt-3 text-xs font-mono">
                  <span className="flex items-center gap-1.5 text-white/50"><span className="w-2.5 h-2.5 rounded-full" style={{ background: ACCENT }} /> Views</span>
                  <span className="flex items-center gap-1.5 text-white/50"><span className="w-2.5 h-2.5 rounded-full" style={{ background: BRAND }} /> Unique Visitors</span>
                </div>
              </div>
            )}
            <div>
              <p className="text-xs font-mono uppercase tracking-widest text-white/50 mb-3">Traffic Sources</p>
              {data.sources.length === 0 ? <EmptyState label="No data yet." /> : (
                <div className="border border-[var(--border-default)] divide-y divide-[var(--border-default)]">
                  {data.sources.map((s, i) => (
                    <div key={s.source} data-testid={`page-trend-source-${i}`} className="flex items-center justify-between p-3 text-sm">
                      <span className="text-white">{s.source}</span>
                      <span className="text-[var(--brand-primary)] font-mono text-xs">{s.count}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function BehaviourTab({ range }) {
  const [data, setData] = useState(null);
  const [drilldownProduct, setDrilldownProduct] = useState(null);
  const [drilldownPage, setDrilldownPage] = useState(null);
  const [trendPage, setTrendPage] = useState(null);
  const [showDigestPreview, setShowDigestPreview] = useState(false);
  useEffect(() => { getAnalyticsBehaviour(range).then(setData); }, [range]);
  if (!data) return <EmptyState label="Loading…" />;
  return (
    <div data-testid="analytics-behaviour-tab">
      <ReturningVisitorDialog product={drilldownProduct} range={range} onClose={() => setDrilldownProduct(null)} />
      <PageReturningVisitorDialog page={drilldownPage} range={range} onClose={() => setDrilldownPage(null)} />
      <PageTrendDialog page={trendPage} onClose={() => setTrendPage(null)} />
      <WeeklyDigestPreviewDialog open={showDigestPreview} onClose={() => setShowDigestPreview(false)} />
      <div className="flex justify-end mb-4">
        <button
          type="button"
          onClick={() => setShowDigestPreview(true)}
          data-testid="preview-weekly-digest-button"
          className="flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-white/60 border border-[var(--border-default)] rounded px-3 py-2 hover:text-[var(--brand-primary)] hover:border-[var(--brand-primary)]/40 transition-colors"
        >
          <Mail size={13} /> Preview This Week's Digest
        </button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
        <div>
          <p className="text-xs font-mono uppercase tracking-widest text-white/50 mb-3">Most Viewed Pages</p>
          {data.topPages.length === 0 ? <EmptyState label="No page views tracked yet." /> : (
            <div className="border border-[var(--border-default)] divide-y divide-[var(--border-default)]">
              {data.topPages.map((p, i) => (
                <div
                  key={p.path || i}
                  data-testid={`analytics-top-page-${i}`}
                  onClick={() => setTrendPage(p)}
                  title="Click to see the views trend, traffic sources, and period-over-period change for this page"
                  className="flex items-center justify-between p-3 text-sm gap-3 cursor-pointer hover:bg-white/5 transition-colors"
                >
                  <span className="text-white font-mono truncate">{p.path}</span>
                  <div className="flex items-center gap-4 shrink-0 text-xs font-mono">
                    <span className="text-white/40">avg {fmtDuration(p.avgTimeOnPageSeconds)}</span>
                    <span className="text-white/40">median {fmtDuration(p.medianTimeOnPageSeconds)}</span>
                    {p.avgScrollDepthPercent != null && <span className="text-white/40 flex items-center gap-1"><ArrowDownToLine size={11} /> {p.avgScrollDepthPercent}%</span>}
                    <span className="text-white/40" data-testid={`analytics-top-page-unique-${i}`}>{p.uniqueViews} unique</span>
                    {p.returningViewers > 0 && (
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); setDrilldownPage(p); }}
                        data-testid={`analytics-top-page-returning-${i}`}
                        title={`${p.returningViewers} visitor${p.returningViewers > 1 ? "s" : ""} came back to view this on a different day — click to see their visits`}
                        className="flex items-center gap-1 shrink-0 text-[10px] font-mono uppercase tracking-wide text-[var(--brand-primary)] border border-[var(--brand-primary)]/40 rounded-full px-2 py-0.5 hover:bg-[var(--brand-primary)]/10 transition-colors cursor-pointer"
                      >
                        <UserCheck size={11} /> {p.returningViewers} returning
                      </button>
                    )}
                    <span className="text-[var(--brand-primary)]">{p.views} views</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        <div>
          <p className="text-xs font-mono uppercase tracking-widest text-white/50 mb-3">Most Viewed Products</p>
          {data.topProducts.length === 0 ? <EmptyState label="No product views tracked yet." /> : (
            <div className="border border-[var(--border-default)] divide-y divide-[var(--border-default)]">
              {data.topProducts.map((p, i) => (
                <div key={p.name || i} data-testid={`analytics-top-product-${i}`} className="flex items-center gap-3 p-3">
                  {p.thumb && <img src={p.thumb} alt={p.name} className="w-10 h-10 object-cover shrink-0" />}
                  <span className="text-white text-sm truncate flex-1">{p.name}</span>
                  {p.returningViewers > 0 && (
                    <button
                      type="button"
                      onClick={() => setDrilldownProduct(p)}
                      data-testid={`analytics-top-product-returning-${i}`}
                      title={`${p.returningViewers} visitor${p.returningViewers > 1 ? "s" : ""} came back to view this on a different day — click to see their visits`}
                      className="flex items-center gap-1 shrink-0 text-[10px] font-mono uppercase tracking-wide text-[var(--brand-primary)] border border-[var(--brand-primary)]/40 rounded-full px-2 py-0.5 hover:bg-[var(--brand-primary)]/10 transition-colors cursor-pointer"
                    >
                      <UserCheck size={11} /> {p.returningViewers} returning
                    </button>
                  )}
                  <div className="flex items-center gap-3 shrink-0 text-xs font-mono">
                    <span className="text-white/40" data-testid={`analytics-top-product-unique-${i}`}>{p.uniqueViews} unique</span>
                    <span className="text-[var(--brand-primary)] text-sm">{p.views} views</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
        <div>
          <p className="text-xs font-mono uppercase tracking-widest text-white/50 mb-3 flex items-center gap-2">
            <LogIn size={14} /> Entry Pages (where people land)
          </p>
          <PathList items={data.entryPages} testIdPrefix="analytics-entry-page" emptyLabel="No sessions tracked yet." />
        </div>
        <div>
          <p className="text-xs font-mono uppercase tracking-widest text-white/50 mb-3 flex items-center gap-2">
            <BounceIcon size={14} /> Exit Pages (where people leave)
          </p>
          <PathList items={data.exitPages} testIdPrefix="analytics-exit-page" emptyLabel="No sessions tracked yet." />
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
        <div>
          <p className="text-xs font-mono uppercase tracking-widest text-white/50 mb-3 flex items-center gap-2">
            <Route size={14} /> After The Homepage, People Go To
          </p>
          <PathList items={data.homepageNextPages} testIdPrefix="analytics-homepage-next" emptyLabel="Not enough data yet — most people leave right after the homepage, or haven't landed there this period." />
        </div>
        <div>
          <p className="text-xs font-mono uppercase tracking-widest text-white/50 mb-3 flex items-center gap-2">
            <MousePointerClick size={14} /> Top Clicked Buttons
          </p>
          {data.topEvents.length === 0 ? <EmptyState label="No button clicks tracked yet (e.g. Buy Now, Enquiry Form)." /> : (
            <div className="border border-[var(--border-default)] divide-y divide-[var(--border-default)]">
              {data.topEvents.map((e, i) => (
                <div key={e.name || i} data-testid={`analytics-top-event-${i}`} className="flex items-center justify-between p-3 text-sm">
                  <span className="text-white capitalize">{e.name.replace(/_/g, " ")}</span>
                  <span className="font-mono text-[var(--brand-primary)]">{e.count}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      <div className="mb-8">
        <p className="text-xs font-mono uppercase tracking-widest text-white/50 mb-3 flex items-center gap-2">
          <MousePointerClick size={14} /> Top Clicks By Page
        </p>
        {data.topClicksByPage.length === 0 ? <EmptyState label="No button clicks tracked yet (e.g. Buy Now, Enquiry Form)." /> : (
          <div className="border border-[var(--border-default)] divide-y divide-[var(--border-default)]">
            {data.topClicksByPage.map((c, i) => (
              <div key={`${c.name}-${c.path}` || i} data-testid={`analytics-top-click-page-${i}`} className="flex items-center justify-between p-3 text-sm gap-3">
                <div className="flex-1 min-w-0 flex items-baseline gap-2">
                  <span className="text-white capitalize shrink-0">{c.name.replace(/_/g, " ")}</span>
                  <span className="text-white/30 font-mono text-xs truncate">on {c.path}</span>
                </div>
                <span className="font-mono text-[var(--brand-primary)] shrink-0">{c.count}</span>
              </div>
            ))}
          </div>
        )}
      </div>
      <div>
        <p className="text-xs font-mono uppercase tracking-widest text-white/50 mb-3 flex items-center gap-2">
          <Route size={14} /> Top Navigation Flows
        </p>
        {!data.navigationFlow || data.navigationFlow.length === 0 ? <EmptyState label="Not enough data yet to map multi-step navigation flows." /> : (
          <div className="flex items-start gap-2 overflow-x-auto pb-2" data-testid="analytics-navigation-flow">
            {data.navigationFlow.map((step, i) => (
              <div key={step.step} className="flex items-center gap-2 shrink-0">
                {i > 0 && <ChevronRight size={16} className="text-white/20 shrink-0" />}
                <FlowStep step={step} />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function AbandonedCartDetailDialog({ sessionId, onClose }) {
  const [detail, setDetail] = useState(null);
  useEffect(() => {
    if (sessionId) getAbandonedCartDetail(sessionId).then(setDetail);
    else setDetail(null);
  }, [sessionId]);

  return (
    <Dialog open={!!sessionId} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-lg bg-[var(--bg-secondary)] border-[var(--border-default)] text-white" data-testid="abandoned-cart-detail-dialog">
        <DialogHeader>
          <DialogTitle className="text-white">Abandoned Cart Detail</DialogTitle>
        </DialogHeader>
        {!detail ? <EmptyState label="Loading…" /> : (
          <div className="space-y-5">
            <div>
              <p className="text-xs font-mono uppercase tracking-widest text-white/50 mb-2">Cart Makeup</p>
              <div className="border border-[var(--border-default)] p-4 space-y-3 text-sm" data-testid="abandoned-cart-items">
                {detail.items && detail.items.length > 0 ? (
                  <div className="space-y-2">
                    {detail.items.map((item, i) => (
                      <div key={i} className="flex justify-between gap-3" data-testid={`abandoned-cart-item-${i}`}>
                        <span className="text-white">{item.product_name}</span>
                        <span className="text-white/60 font-mono text-xs whitespace-nowrap shrink-0">
                          Qty {item.quantity} · {fmtMoney(item.unit_amount_cents * item.quantity)}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-white">{detail.product_name}</p>
                )}
                <div className="flex justify-between text-white/60 font-mono text-xs border-t border-[var(--border-default)] pt-2">
                  <span>Total Qty {detail.quantity}</span>
                  <span className="text-[var(--brand-primary)]">{fmtMoney(detail.amount_total_cents)}</span>
                </div>
                {detail.order_notes && <p className="text-white/50 text-xs italic">Note: {detail.order_notes}</p>}
                <p className="text-white/40 text-xs font-mono">Started {timeAgo(detail.created_at)}</p>
              </div>
            </div>
            <div>
              <p className="text-xs font-mono uppercase tracking-widest text-white/50 mb-2">Customer Details</p>
              {detail.customer?.email || detail.customer?.phone || detail.customer?.name ? (
                <div className="border border-[var(--border-default)] p-4 space-y-2 text-sm">
                  {detail.customer?.name && <p className="text-white">{detail.customer.name}</p>}
                  {detail.customer?.email && (
                    <p className="text-white/70 flex items-center gap-2" data-testid="abandoned-cart-email"><Mail size={13} /> {detail.customer.email}</p>
                  )}
                  {detail.customer?.phone && (
                    <p className="text-white/70 flex items-center gap-2" data-testid="abandoned-cart-phone"><Phone size={13} /> {detail.customer.phone}</p>
                  )}
                  {detail.customer?.address && <p className="text-white/50 text-xs">{detail.customer.address}</p>}
                </div>
              ) : (
                <p className="text-white/40 text-xs font-mono italic">Not captured — customer left before entering any details.</p>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function AbandonedCartsTab() {
  const [data, setData] = useState(null);
  const [activeSessionId, setActiveSessionId] = useState(null);
  useEffect(() => { getAnalyticsAbandonedCarts().then(setData); }, []);
  if (!data) return <EmptyState label="Loading…" />;
  return (
    <div data-testid="analytics-abandoned-tab">
      <p className="text-white/50 text-sm font-mono mb-6">Checkouts started but never completed — click any row for the full cart + contact details.</p>
      {data.carts.length === 0 ? (
        <EmptyState label="No abandoned checkouts right now — nice!" />
      ) : (
        <div className="border border-[var(--border-default)] divide-y divide-[var(--border-default)]">
          {data.carts.map((c, i) => (
            <button
              key={c.id}
              data-testid={`analytics-abandoned-cart-${i}`}
              onClick={() => setActiveSessionId(c.session_id)}
              className="w-full flex items-center justify-between p-4 gap-4 text-left hover:bg-white/5 transition-colors"
            >
              <div className="flex-1">
                <div className="text-white text-sm">{c.product_name}</div>
                <div className="text-white/40 text-xs font-mono mt-1">Qty {c.quantity} · {timeAgo(c.created_at)}</div>
              </div>
              <div className="text-[var(--brand-primary)] font-mono text-sm shrink-0">{fmtMoney(c.amount_total_cents)}</div>
            </button>
          ))}
        </div>
      )}
      <AbandonedCartDetailDialog sessionId={activeSessionId} onClose={() => setActiveSessionId(null)} />
    </div>
  );
}

const DEVICE_ICON = { mobile: Smartphone, tablet: Tablet, desktop: Monitor };

// Two-tone chime, generated on the fly via the Web Audio API — no audio
// asset to host/manage. Silently no-ops if the browser blocks autoplay
// (e.g. before any user gesture has happened on the page).
function playAlertPing() {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const tone = (freq, start, duration) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "sine";
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0.0001, ctx.currentTime + start);
      gain.gain.exponentialRampToValueAtTime(0.3, ctx.currentTime + start + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + start + duration);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(ctx.currentTime + start);
      osc.stop(ctx.currentTime + start + duration);
    };
    tone(880, 0, 0.15);
    tone(1320, 0.12, 0.18);
  } catch (e) {
    // audio blocked (e.g. no user gesture yet) — non-fatal, log for visibility
    console.warn("playAlertPing: audio alert blocked", e);
  }
}

function notifyDesktop(title, body) {
  if (typeof Notification !== "undefined" && Notification.permission === "granted") {
    try {
      new Notification(title, { body, icon: "/logos/xpeditionz-logo.png" });
    } catch (e) {
      console.warn("notifyDesktop: failed to show desktop notification", e);
    }
  }
}

// Generic session page-history dialog — used both for the Live tab's
// currently-active visitors AND for drilling into a past returning-visitor
// session from the Traffic Overview (the underlying endpoint works for any
// session_id regardless of whether it's currently live).
// Merges each page's "exit" event (which just logs how long they stayed) into
// the pageview/click row for that same page, instead of showing it as its own
// row — a bare exit event looks identical to a second visit to the same page,
// which is confusing (it actually means they left after that many seconds).
function mergeExitEvents(events) {
  const rows = [];
  events.forEach((e, i) => {
    if (e.event_type === "exit") {
      const match = [...rows].reverse().find((r) => r.path === e.path && r.durationSeconds == null);
      if (match) {
        match.durationSeconds = e.duration_seconds;
        match.isLastEvent = i === events.length - 1;
        return;
      }
    }
    rows.push({ ...e, durationSeconds: null, isLastEvent: false });
  });
  return rows;
}

function VisitorHistoryDialog({ sessionId, onClose }) {
  const [detail, setDetail] = useState(null);
  useEffect(() => {
    if (!sessionId) { setDetail(null); return; }
    getAnalyticsLiveHistory(sessionId).then(setDetail);
  }, [sessionId]);
  return (
    <Dialog open={!!sessionId} onOpenChange={(open) => !open && onClose()}>
      <DialogContent data-testid="live-visitor-history-dialog" className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-display text-xl text-white">Visitor History</DialogTitle>
          <DialogDescription className="sr-only">Chronological list of pages and clicks for this visitor's session.</DialogDescription>
        </DialogHeader>
        {!detail ? (
          <EmptyState label="Loading…" />
        ) : (
          <div className="space-y-3">
            <p className="text-white/50 text-xs font-mono uppercase tracking-widest">Arrived via {detail.source}</p>
            <div className="border border-[var(--border-default)] divide-y divide-[var(--border-default)] max-h-80 overflow-y-auto">
              {mergeExitEvents(detail.events).map((e, i) => (
                <div key={i} data-testid={`live-history-event-${i}`} className="flex items-center justify-between p-3 text-sm gap-3">
                  <div className="flex-1 min-w-0">
                    <p className="text-white truncate">{e.path}</p>
                    {e.event_type === "click" && <p className="text-[var(--brand-primary)] text-xs font-mono capitalize">{(e.event_name || "").replace(/_/g, " ")}</p>}
                    {e.durationSeconds != null && <p className="text-white/40 text-xs font-mono">stayed {fmtDuration(e.durationSeconds)}{e.isLastEvent ? " · left the site" : ""}</p>}
                  </div>
                  <span className="text-white/40 text-xs font-mono shrink-0">{new Date(e.created_at).toLocaleTimeString("en-NZ", { hour: "2-digit", minute: "2-digit", second: "2-digit" })}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function LiveTab() {
  const [data, setData] = useState(null);
  const [activeSessionId, setActiveSessionId] = useState(null);
  const [alertsEnabled, setAlertsEnabled] = useState(() => localStorage.getItem("xpz_live_alerts") === "1");
  const seenHighIntent = useRef(new Set());
  const seenAlerts = useRef(new Set());

  useEffect(() => {
    const load = () =>
      getAnalyticsLive().then((res) => {
        if (alertsEnabled) {
          res.visitors.forEach((v) => {
            if (v.is_high_intent && !seenHighIntent.current.has(v.session_id)) {
              seenHighIntent.current.add(v.session_id);
              playAlertPing();
              notifyDesktop("Visitor on Checkout", `Someone is on the checkout page right now.`);
            }
          });
          res.alerts.forEach((a) => {
            const alertKey = `${a.session_id}-${a.created_at}`;
            if (!seenAlerts.current.has(alertKey)) {
              seenAlerts.current.add(alertKey);
              playAlertPing();
              notifyDesktop(
                a.event_name === "enquiry_form_submit" ? "New Enquiry Submitted" : "Buy Now Clicked",
                `On ${a.path}`
              );
            }
          });
        }
        setData(res);
      });
    load();
    const interval = setInterval(load, 8000);
    return () => clearInterval(interval);
  }, [alertsEnabled]);

  const enableAlerts = async () => {
    if (typeof Notification !== "undefined" && Notification.permission !== "granted") {
      await Notification.requestPermission();
    }
    playAlertPing();
    localStorage.setItem("xpz_live_alerts", "1");
    setAlertsEnabled(true);
  };

  if (!data) return <EmptyState label="Loading…" />;

  return (
    <div data-testid="analytics-live-tab">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <span className="relative flex h-2.5 w-2.5">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-60" />
            <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-400" />
          </span>
          <span data-testid="analytics-live-count" className="font-display text-3xl text-white">{data.count}</span>
          <span className="text-white/50 text-xs font-mono uppercase tracking-widest">{data.count === 1 ? "person" : "people"} on site right now</span>
        </div>
        <button
          type="button"
          onClick={enableAlerts}
          disabled={alertsEnabled}
          data-testid="live-alerts-enable-btn"
          className="btn-secondary disabled:opacity-50 disabled:cursor-default"
        >
          {alertsEnabled ? "Alerts On — Checkout & Enquiries" : "Enable Alerts for Checkout & Enquiries"}
        </button>
      </div>

      {data.visitors.length === 0 ? (
        <EmptyState label="No one's browsing the site right now — check back shortly." />
      ) : (
        <div className="border border-[var(--border-default)] divide-y divide-[var(--border-default)]">
          {data.visitors.map((v, i) => {
            const DeviceIcon = DEVICE_ICON[v.device] || Monitor;
            const location = v.city && v.country ? `${v.city}, ${v.country}` : v.country || "Unknown location";
            return (
              <button
                key={v.session_id}
                data-testid={`analytics-live-visitor-${i}`}
                onClick={() => setActiveSessionId(v.session_id)}
                className={`w-full flex items-center justify-between p-4 gap-4 text-left hover:bg-white/5 transition-colors ${v.is_high_intent ? "border-l-2 border-[var(--brand-primary)] bg-[var(--brand-primary)]/5" : ""}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-white text-sm truncate">{v.current_path}</p>
                    {v.is_high_intent && <span data-testid={`analytics-live-visitor-${i}-checkout-badge`} className="text-[10px] font-mono uppercase tracking-widest text-[var(--brand-primary)] border border-[var(--brand-primary)]/50 px-1.5 py-0.5 shrink-0">Checkout</span>}
                  </div>
                  <div className="flex items-center gap-3 mt-1 text-white/40 text-xs font-mono">
                    <span className="flex items-center gap-1"><MapPin size={12} /> {location}</span>
                    <span className="flex items-center gap-1"><DeviceIcon size={12} /> {v.device}</span>
                    <span className="flex items-center gap-1"><Clock size={12} /> here {fmtDuration(Math.round((Date.now() - new Date(v.first_seen).getTime()) / 1000))}</span>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      )}
      <VisitorHistoryDialog sessionId={activeSessionId} onClose={() => setActiveSessionId(null)} />
    </div>
  );
}

function ResetAnalyticsButton() {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const handleConfirm = async () => {
    setLoading(true);
    try {
      const res = await resetAnalytics();
      setResult(res);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <button
        type="button"
        data-testid="analytics-reset-btn"
        onClick={() => { setOpen(true); setResult(null); }}
        className="text-xs font-mono uppercase tracking-widest text-white/40 hover:text-red-400 transition-colors underline"
      >
        Reset Analytics Data
      </button>
      <Dialog open={open} onOpenChange={(o) => { if (!loading) setOpen(o); }}>
        <DialogContent data-testid="analytics-reset-dialog" className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-xl text-white">Reset Analytics Data</DialogTitle>
            <DialogDescription className="text-white/60">
              This permanently deletes all tracked page views, sessions and click events, AND all order/payment
              records (Sales Overview + Abandoned Carts) — for clearing out pre-launch test traffic and test orders
              so your dashboard starts clean. It does NOT touch product/catalog data. This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          {result ? (
            <div data-testid="analytics-reset-success" className="text-emerald-400 text-sm font-mono">
              Cleared {result.eventsDeleted} events, {result.visitorsDeleted} live-visitor records and {result.ordersDeleted} orders.
            </div>
          ) : (
            <div className="flex gap-3 justify-end mt-2">
              <button type="button" onClick={() => setOpen(false)} disabled={loading} className="btn-secondary" data-testid="analytics-reset-cancel">
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirm}
                disabled={loading}
                data-testid="analytics-reset-confirm"
                className="px-4 py-2 text-xs font-mono uppercase tracking-widest border border-red-500 text-red-400 hover:bg-red-500 hover:text-white transition-colors disabled:opacity-50"
              >
                {loading ? "Clearing…" : "Yes, Clear Everything"}
              </button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

export default function AdminAnalyticsPage() {
  const [tab, setTab] = useState("sales");
  const [range, setRange] = useState("30d");

  return (
    <div data-testid="admin-analytics-page">
      <div className="flex items-center justify-between gap-4 mb-2">
        <div className="flex items-center gap-2">
          <BarChart3 size={20} className="text-[var(--brand-primary)]" />
          <h1 className="font-display text-3xl text-white">Analytics</h1>
        </div>
        <ResetAnalyticsButton />
      </div>
      <p className="text-white/50 text-sm font-mono mb-6">Sales, traffic and shopper behaviour — tracked natively, no external service.</p>

      <div className="flex flex-wrap items-center justify-between gap-4 mb-6 border-b border-[var(--border-default)] pb-4">
        <div className="flex items-center gap-1 overflow-x-auto">
          {TABS.map((t) => (
            <button
              key={t.key}
              data-testid={`analytics-tab-${t.key}`}
              onClick={() => setTab(t.key)}
              className={`shrink-0 px-3 py-1.5 text-xs font-mono uppercase tracking-widest border transition-colors ${
                tab === t.key ? "border-[var(--brand-primary)] text-[var(--brand-primary)]" : "border-transparent text-white/50 hover:text-white"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
        {tab !== "abandoned" && tab !== "live" && <RangeSelector range={range} setRange={setRange} />}
      </div>

      {tab === "sales" && <SalesTab range={range} />}
      {tab === "traffic" && <TrafficTab range={range} />}
      {tab === "behaviour" && <BehaviourTab range={range} />}
      {tab === "live" && <LiveTab />}
      {tab === "abandoned" && <AbandonedCartsTab />}
    </div>
  );
}
